/**
 * Substitute one set of literal colours for another across a section's CSS and
 * inline styles.
 *
 * WHY THIS EXISTS AT ALL. Writing `state.theme` repaints only what reads a
 * `var()`. Compose-time `skinCSS` (`ai/brand-skin.js`) already substituted the
 * template's authored hexes for *literal* theme hexes when the project was
 * built, so those literals do not move when the theme does — and the result of
 * a theme-only rebrand is a HALF-BRANDED site, the visible half being whichever
 * colours the template happened to hardcode. This module moves the other half.
 *
 * It is the third consumer of the same idea and deliberately NOT a fourth
 * implementation of the walk: `brand-skin.js` does exactly this at compose time
 * with the same `Hash`-node substitution. What is new here is that it also
 * handles `rgb()`/`hsl()` forms and inline `style` attributes, because a
 * template author writes `rgb(255, 106, 62)` about as often as `#ff6a3e` and a
 * pass that misses it leaves a visibly wrong colour on the page.
 *
 * PURE. The caller injects `parseHTML`, so this runs under jsdom, in the
 * browser, and in a Worker without change. It never touches the store.
 *
 * WHAT IT CANNOT DO, stated because the limit is real and the UI must not
 * promise otherwise: it matches on VALUE, so a colour the user hand-picked that
 * happens to equal the outgoing `--primary` is indistinguishable from one the
 * theme put there, and gets recoloured. Per-declaration provenance would be
 * needed to tell them apart, and that is a much larger feature.
 */

import * as csstree from '../vendor/csstree.esm.js';
import { normalizeHexColor } from './theme-tokens.js';
import { colorToHex } from './color-utils.js';

/** Colour functions worth converting. `color()` and `lab()` are out of scope. */
const COLOR_FUNCTIONS = new Set(['rgb', 'rgba', 'hsl', 'hsla']);

function lookup(map, hex) {
  if (!hex) return null;
  return map.get(hex.toLowerCase()) || null;
}

/**
 * Rewrite an already-parsed AST in place.
 * @returns {number} how many nodes changed
 */
function recolorAst(ast, map) {
  let changed = 0;
  csstree.walk(ast, (node) => {
    if (node.type === 'Hash') {
      const replacement = lookup(map, normalizeHexColor(`#${node.value}`));
      if (replacement) {
        node.value = replacement.slice(1);
        changed += 1;
      }
      return;
    }
    if (node.type === 'Function' && COLOR_FUNCTIONS.has(String(node.name).toLowerCase())) {
      let hex;
      try {
        hex = colorToHex(csstree.generate(node));
      } catch {
        return; // an unparseable colour is left exactly as authored
      }
      // A TRANSLUCENT colour is left alone. `colorToHex` returns 8 digits when
      // alpha < 1, and rewriting the node as a 6-digit Hash would silently make
      // a 50%-opacity overlay fully opaque. The map is 6-digit throughout, so
      // an 8-digit value would never match anyway — but relying on that is the
      // kind of implicit coupling that breaks the day someone adds alpha
      // support to the map, so the guard is explicit.
      if (!hex || hex.length !== 7) return;
      const replacement = lookup(map, hex);
      if (!replacement) return;
      // Rewrite the node in place as a Hash. `generate()` switches on `type`,
      // so this is a real conversion rather than a cosmetic one — and it is
      // why `rgba()` with alpha is deliberately excluded below.
      node.type = 'Hash';
      node.value = replacement.slice(1);
      delete node.name;
      delete node.children;
      changed += 1;
    }
  });
  return changed;
}

/**
 * Recolor a stylesheet.
 *
 * @param {string} css
 * @param {Map<string,string>} map outgoing hex (lowercase) → incoming hex
 * @returns {{css: string, changed: number}}
 */
export function recolorCss(css, map) {
  if (!css || !map?.size) return { css: css || '', changed: 0 };
  try {
    // `parseCustomProperty` matters: without it a `--brand: #ff6a3e` declaration
    // parses as an opaque Raw node and the value inside is never visited.
    const ast = csstree.parse(css, { parseCustomProperty: true });
    const changed = recolorAst(ast, map);
    if (!changed) return { css, changed: 0 };
    return { css: csstree.generate(ast), changed };
  } catch {
    // A BACKSTOP, not the primary path. css-tree is extremely tolerant —
    // verified against the vendored build, it threw on none of `.a{color:`,
    // `}}}{{{`, `@media` or `.a{color:red}}}}`, parsing each into Raw nodes
    // instead. So malformed CSS is normally handled by the `changed === 0`
    // early return above, which hands back the ORIGINAL string.
    //
    // Kept anyway: losing a stylesheet is far worse than missing a recolor, and
    // a future parser upgrade could start throwing where this one does not.
    return { css, changed: 0 };
  }
}

/** Recolor one inline `style="…"` value (a declaration list, not a stylesheet). */
export function recolorDeclarations(value, map) {
  if (!value || !map?.size) return { value: value || '', changed: 0 };
  try {
    const ast = csstree.parse(value, { context: 'declarationList', parseCustomProperty: true });
    const changed = recolorAst(ast, map);
    if (!changed) return { value, changed: 0 };
    return { value: csstree.generate(ast), changed };
  } catch {
    return { value, changed: 0 };
  }
}

/**
 * Recolor a section's HTML: every inline `style` attribute and every embedded
 * `<style>` block.
 *
 * The two are parsed with different css-tree contexts — a declaration list
 * versus a full stylesheet — because parsing one as the other silently yields
 * nothing rather than failing.
 *
 * @param {string} html
 * @param {Map<string,string>} map
 * @param {{parseHTML: (html: string) => Document}} deps
 * @returns {{html: string, changed: number}}
 */
export function recolorHtml(html, map, { parseHTML } = {}) {
  if (!html || !map?.size || typeof parseHTML !== 'function') {
    return { html: html || '', changed: 0 };
  }

  let doc;
  try {
    doc = parseHTML(`<div id="pb-recolor-root">${html}</div>`);
  } catch {
    return { html, changed: 0 };
  }
  const root = doc?.getElementById?.('pb-recolor-root');
  if (!root) return { html, changed: 0 };

  let changed = 0;
  for (const el of root.querySelectorAll('[style]')) {
    const out = recolorDeclarations(el.getAttribute('style'), map);
    if (out.changed) {
      el.setAttribute('style', out.value);
      changed += out.changed;
    }
  }
  for (const styleEl of root.querySelectorAll('style')) {
    const out = recolorCss(styleEl.textContent, map);
    if (out.changed) {
      styleEl.textContent = out.css;
      changed += out.changed;
    }
  }

  if (!changed) return { html, changed: 0 };
  return { html: root.innerHTML, changed };
}

/**
 * Recolor one section, returning only what actually changed.
 *
 * Returns the ORIGINAL strings when nothing matched, so a caller can compare by
 * identity and skip writing a patch — a no-op write would still cost a history
 * entry and a collab frame.
 *
 * @returns {{html: string, css: string, changed: number}}
 */
export function recolorSection(section, map, deps = {}) {
  const htmlOut = recolorHtml(section?.html || '', map, deps);
  const cssOut = recolorCss(section?.css || '', map);
  return {
    html: htmlOut.html,
    css: cssOut.css,
    changed: htmlOut.changed + cssOut.changed
  };
}

/**
 * Count what a recolor WOULD touch, without producing new strings.
 *
 * The confirm dialog needs a number before the user commits, and generating
 * every section's CSS twice to get it is wasteful on a large project.
 */
export function countRecolorMatches(sections, map, deps = {}) {
  let sectionsAffected = 0;
  let total = 0;
  for (const section of sections || []) {
    const { changed } = recolorSection(section, map, deps);
    if (changed) {
      sectionsAffected += 1;
      total += changed;
    }
  }
  return { sectionsAffected, total };
}
